import { getUrl } from 'helpers';
import { useCallback, useEffect, useState } from 'react';
import {
    Container,
    ContinueButton,
    Content,
    CopyButton,
    CopyButtonTooltip,
    ImageContainer,
    Link,
    Title,
} from './MobileWarning.styled';

const DISMISS_STORAGE_KEY = 'mobile-warning-dismissed';

export default function MobileWarning({ children, minWidth }: MobileWarningProps) {
    const [copied, setCopied] = useState(false);
    const [linkParts, setLinkParts] = useState(['', '']);
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const handleResize = useCallback(() => {
        const newVisible = window.innerWidth < minWidth;
        setVisible(newVisible);
    }, [minWidth]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        handleResize();

        try {
            if (window.sessionStorage?.getItem(DISMISS_STORAGE_KEY) === 'true') {
                setDismissed(true);
            }
        } catch {
            // sessionStorage unavailable (private mode, disabled) — fall back to non-persisted state.
        }

        // Add listener
        window.addEventListener('resize', handleResize);

        // Get link parts
        const link = `${window.location.hostname}${getUrl('')}`;
        const newLinkParts = ['', ''];
        link.split('/').forEach(segment => {
            const index = newLinkParts[0].length + segment.length < link.length / 2 ? 0 : 1;
            newLinkParts[index] += (index || newLinkParts[index].length ? '/' : '') + segment;
        });
        setLinkParts(newLinkParts);

        return () => {
            // Remove listener
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    async function handleCopy() {
        const writeText = navigator.clipboard?.writeText?.bind(navigator.clipboard);
        if (!writeText) {
            return;
        }

        try {
            await writeText(`${window.location.origin}${process.env.NEXT_CONFIG_BASE_PATH || ''}`);
        } catch {
            return;
        }

        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    }

    function handleContinue() {
        setDismissed(true);
        try {
            window.sessionStorage?.setItem(DISMISS_STORAGE_KEY, 'true');
        } catch {
            // sessionStorage unavailable — dismissal will not persist across route changes that remount.
        }
    }

    if (!visible || dismissed) {
        return <>{children}</>;
    }

    return (
        <Container>
            <Content>
                <Title data-testid="mobile-warning-message">
                    This game is optimized for screen sizes of ~{minWidth} pixels, try it on your desktop.
                </Title>
                <ImageContainer>
                    <picture>
                        <img
                            alt="Man sitting in front of a desktop computer"
                            src={getUrl('/assets/mobile-warning.png')}
                        />
                    </picture>
                </ImageContainer>
                <Link>
                    {linkParts[0]}
                    <br />
                    {linkParts[1]}
                </Link>
                <CopyButton
                    aria-label="Copy link to clipboard"
                    data-testid="mobile-warning-copy"
                    onClick={handleCopy}
                    type="button"
                >
                    <picture>
                        <img alt="" aria-hidden="true" src={getUrl('/assets/mobile-warning-copy.svg')} />
                    </picture>
                    {copied && (
                        <CopyButtonTooltip data-testid="mobile-warning-copied-tooltip">Copied</CopyButtonTooltip>
                    )}
                </CopyButton>
                <ContinueButton
                    data-testid="mobile-warning-continue"
                    onClick={handleContinue}
                    type="button"
                >
                    Continue anyway
                </ContinueButton>
            </Content>
        </Container>
    );
}
